// ============================================================
// API Client — reusable fetch wrapper for API calls
// ============================================================

const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, string[]>,
    // Extra payload carried through as-is (e.g. validation errors, duplicate claim info)
    public payload?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, string[]>;
    [key: string]: unknown; // allow extra fields like `errors`, `existingClaimId`
  };
}

// Fired whenever any API call receives a 401 — SessionWatcher listens for this
export const SESSION_EXPIRED_EVENT = 'rhpc:session-expired';

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const body: ApiResponse<T> = await response.json();

  if (!response.ok || !body.success) {
    const { code, message, details, ...rest } = body.error ?? {};

    // Broadcast session expiry so SessionWatcher can redirect to login
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }

    throw new ApiError(
      response.status,
      code ?? 'UNKNOWN_ERROR',
      message ?? 'An unexpected error occurred',
      details,
      Object.keys(rest).length > 0 ? rest as Record<string, unknown> : undefined
    );
  }

  return body.data as T;
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),

  post: <T>(endpoint: string, data?: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  postBlob: async (endpoint: string, data?: unknown) => {
    const url = `${API_BASE}${endpoint}`;
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: data ? JSON.stringify(data) : undefined,
    });
    if (!response.ok) throw new Error('Failed to fetch blob');
    
    const contentDisposition = response.headers.get('content-disposition') || '';
    const blob = await response.blob();
    return { blob, contentDisposition };
  },

  put: <T>(endpoint: string, data: unknown) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  patch: <T>(endpoint: string, data: unknown) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: <T>(endpoint: string) =>
    request<T>(endpoint, { method: 'DELETE' }),
};
